import React, { useState } from 'react'

import { type HomepageContent } from '../../types'
import YouTubePlayer from './YouTubePlayer/YouTubePlayer'
import styles from './styles.module.scss'
import { resolveVideoSource } from './videoSource'

type IntelligenceProps = {
  content: HomepageContent['intelligence']
}

type IntelligenceVideoProps = Pick<HomepageContent['intelligence'], 'videoUrl' | 'posterImage'>

const IntelligenceVideo = ({ videoUrl, posterImage }: IntelligenceVideoProps) => {
  const videoSource = resolveVideoSource(videoUrl)
  const [isActive, setIsActive] = useState(false)
  const [fileFailed, setFileFailed] = useState(false)
  const playGraphic = (
    <img className={styles.playIcon} src="/images/home/intelligence-play.svg" alt="" />
  )

  const renderMedia = () => {
    if (isActive && videoSource?.kind === 'youtube') {
      return (
        <YouTubePlayer
          videoId={videoSource.videoId}
          title="Urus Advisory video"
          posterImage={posterImage}
        />
      )
    }

    if (isActive && videoSource?.kind === 'embed') {
      return (
        <iframe
          className={styles.player}
          src={videoSource.embedUrl}
          title="Urus Advisory video"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      )
    }

    if (isActive && videoSource?.kind === 'file' && !fileFailed) {
      return (
        <video
          className={styles.player}
          src={videoSource.src}
          poster={posterImage}
          controls
          autoPlay
          playsInline
          onError={() => setFileFailed(true)}
        />
      )
    }

    if (fileFailed && videoSource?.kind === 'file') {
      return (
        <div className={styles.poster}>
          <p className={styles.videoError}>Video is unavailable. Please try again later.</p>
          <a href={videoSource.src}>Open video</a>
        </div>
      )
    }

    if (videoSource?.kind === 'link') {
      return (
        <a
          className={styles.preview}
          href={videoSource.watchUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Watch video"
        >
          {playGraphic}
        </a>
      )
    }

    if (!videoSource) return null

    return (
      <button
        className={styles.preview}
        type="button"
        aria-label="Play video"
        onClick={() => setIsActive(true)}
      >
        {playGraphic}
      </button>
    )
  }

  return (
    <>
      <div className={styles.video} data-active={isActive}>
        <img className={styles.videoBackdrop} src={posterImage} alt="" />
        {renderMedia()}
      </div>
      {isActive && (videoSource?.kind === 'embed' || videoSource?.kind === 'youtube') ? (
        <a
          className={styles.watchLink}
          href={videoSource.watchUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Watch on {videoSource.kind === 'youtube' ? 'YouTube' : videoSource.provider}{' '}
          <span aria-hidden>↗</span>
        </a>
      ) : null}
    </>
  )
}

const Intelligence = ({ content }: IntelligenceProps) => {
  return (
    <section className={styles.section} aria-labelledby="intelligence-title">
      <div className={styles.content}>
        <div className={styles.heading}>
          <h2 id="intelligence-title">{content.title}</h2>
          <p>{content.description}</p>
        </div>
        <IntelligenceVideo videoUrl={content.videoUrl} posterImage={content.posterImage} />
        <div className={styles.points}>
          {content.points.map((point) => (
            <article key={point.title} className={styles.point}>
              <h3>{point.title}</h3>
              <p>{point.description}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

export default Intelligence
